import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { rulesRecapSentence, stepLookupOf, stepTitle } from '@/lib/protocol-labels';
import {
  otherSteps,
  setAttempts,
  setEmptyAnswers,
  setFallback,
  setFallbackTarget,
  setFallbackText,
  setMaxTurns,
} from '@/lib/protocol-step-draft';
import type { ProtocolStepDraft, StepRules } from '@matter_extractor/shared';
import { PROTOCOL_CLOSE_TEXT_MAX, PROTOCOL_DEFAULT_CLOSE_TEXT } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The « règles générales » of a step: the numbers the engine applies without a
 * condition being written (refonte modulaire §7.2, §8.3; maquette `rulesHTML`).
 *
 * ## The cap is required, and the panel says so before the box does
 *
 * A step without a turn cap is a refusal of « Enregistrer une nouvelle version »
 * (§7.1, H8), and the draft holds `null` because a step being typed has no cap
 * yet. So the field is yellow and carries the sentence: the refusal is met here,
 * where it can be fixed in one keystroke, rather than in a dialog three clicks
 * later that can only name it.
 *
 * ## The recap is generated and never typed
 *
 * `rulesRecapSentence` composes it from the four values, and it returns null
 * while the cap is missing - the panel then says the sentence will write itself,
 * rather than showing a sentence with a hole where a number belongs. It is the
 * one place the three numbers, the essais and the repli are read as ONE
 * behaviour, which is how §8.3 describes them.
 *
 * ## The budget is read-only here, on purpose
 *
 * §11.2 makes it ONE global value in euros applied to each step (H3). A number
 * shown beside the three others would read as a fourth one to set, so the line
 * names where it is set instead - and it names no amount, because this lot has
 * no Réglages page to read one from and an invented figure would be worse than
 * none.
 */

export interface StepRulesPanelProps {
  step: ProtocolStepDraft;
  /** Every step: « aller à une étape » chooses among the others. */
  steps: readonly ProtocolStepDraft[];
  readOnly: boolean;
  onChangeRules: (next: StepRules) => void;
}

const NUMBER_BOX =
  'h-9 w-20 rounded-md border-2 bg-white px-2 text-right text-sm read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function StepRulesPanel({ step, steps, readOnly, onChangeRules }: StepRulesPanelProps) {
  const rules = step.rules;
  const others = otherSteps(steps, step.id);
  const alone = others.length === 0;
  const recap = rulesRecapSentence(rules, stepLookupOf(steps));

  return (
    <div>
      <KeyValue
        label="plafond de tours"
        sub="requis · pas de minimum"
        control={
          <input
            type="number"
            min={1}
            data-testid={PROTOCOL_TEST_IDS.ruleMaxTurns}
            aria-label="Plafond de tours"
            value={rules.maxTurns ?? ''}
            readOnly={readOnly}
            placeholder="…"
            onChange={(event) => onChangeRules(setMaxTurns(rules, event.currentTarget.value))}
            className={`${NUMBER_BOX} ${rules.maxTurns === null ? 'border-jaune-deep bg-jaune-tint' : 'border-gray-300'}`}
          />
        }
      />
      {rules.maxTurns === null ? (
        <p className="-mt-1 mb-1.5 inline-flex items-center gap-1.5 text-jaune-deep text-xs">
          <TriangleAlert aria-hidden="true" className="size-3.5" />
          sans plafond, l’enregistrement d’une version refusera cette étape
        </p>
      ) : null}
      <KeyValue
        label="réponses vides consécutives"
        sub="au-delà, le plafond est atteint"
        control={
          <input
            type="number"
            min={0}
            data-testid={PROTOCOL_TEST_IDS.ruleEmptyAnswers}
            aria-label="Réponses vides consécutives"
            value={rules.emptyAnswersAllowed}
            readOnly={readOnly}
            onChange={(event) => onChangeRules(setEmptyAnswers(rules, event.currentTarget.value))}
            className={`${NUMBER_BOX} border-gray-300`}
          />
        }
      />
      <KeyValue
        label="nombre d’essais"
        sub="passages de l’étape en tout, 1 = pas de rejeu"
        control={
          <input
            type="number"
            min={1}
            data-testid={PROTOCOL_TEST_IDS.ruleAttempts}
            aria-label="Nombre d’essais"
            value={rules.attempts}
            readOnly={readOnly}
            onChange={(event) => onChangeRules(setAttempts(rules, event.currentTarget.value))}
            className={`${NUMBER_BOX} border-gray-300`}
          />
        }
      />

      <p className="mt-3.5 mb-2 font-semibold text-gray-800 text-sm">au-delà des essais</p>
      <div role="radiogroup" aria-label="Au-delà des essais" className="grid gap-2">
        <Tile
          testId={PROTOCOL_TEST_IDS.fallbackClose}
          title="fermer l’expérience"
          sub="aucune page suivante · défaut"
          checked={rules.fallback.kind === 'close-experience'}
          disabled={readOnly}
          onPick={() => onChangeRules(setFallback(rules, 'close-experience', []))}
        >
          {rules.fallback.kind === 'close-experience' ? (
            <div className="space-y-1">
              <input
                data-testid={PROTOCOL_TEST_IDS.fallbackText}
                aria-label="Texte de clôture"
                value={rules.fallback.text}
                readOnly={readOnly}
                maxLength={PROTOCOL_CLOSE_TEXT_MAX}
                onChange={(event) =>
                  onChangeRules(setFallbackText(rules, event.currentTarget.value))
                }
                className="h-9 w-full rounded-md border-2 border-gray-300 bg-white px-2.5 text-caption read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="flex flex-wrap items-center justify-between gap-2 text-gray-600 text-xs">
                <span>ce que la personne lit en dernier</span>
                {rules.fallback.text === PROTOCOL_DEFAULT_CLOSE_TEXT || readOnly ? (
                  <span>texte par défaut</span>
                ) : (
                  <button
                    type="button"
                    data-testid={PROTOCOL_TEST_IDS.fallbackTextDefault}
                    onClick={() =>
                      onChangeRules(setFallbackText(rules, PROTOCOL_DEFAULT_CLOSE_TEXT))
                    }
                    className="cursor-pointer text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    remettre le texte par défaut
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </Tile>
        <Tile
          testId={PROTOCOL_TEST_IDS.fallbackGoTo}
          title="aller à une étape"
          sub={alone ? 'aucune autre étape' : 'choisir une étape, avant ou après'}
          checked={rules.fallback.kind === 'go-to-step'}
          disabled={readOnly || alone}
          onPick={() =>
            onChangeRules(
              setFallback(
                rules,
                'go-to-step',
                others.map((one) => one.id),
              ),
            )
          }
        >
          {rules.fallback.kind === 'go-to-step' ? (
            <select
              data-testid={PROTOCOL_TEST_IDS.fallbackTarget}
              aria-label="Étape de repli"
              value={rules.fallback.stepId}
              disabled={readOnly}
              onChange={(event) =>
                onChangeRules(setFallbackTarget(rules, event.currentTarget.value))
              }
              className="h-8 max-w-full rounded-md border-2 border-gray-300 bg-white px-2 text-caption text-gray-800 disabled:bg-gray-50"
            >
              {others.map((one) => (
                <option key={one.id} value={one.id}>
                  {stepTitle({ index: steps.indexOf(one), name: one.name })}
                </option>
              ))}
            </select>
          ) : null}
        </Tile>
      </div>

      <p
        data-testid={PROTOCOL_TEST_IDS.rulesRecap}
        className="mt-3.5 flex gap-2 rounded-md bg-gray-50 px-3 py-2.5 text-caption text-gray-700"
      >
        <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-gray-500" />
        {recap === null ? (
          <span className="text-gray-500">La phrase s’écrira quand le plafond sera posé.</span>
        ) : (
          <span>{recap}</span>
        )}
      </p>

      <p className="mt-2.5 text-gray-500 text-xs">
        budget IA par étape : réglé dans{' '}
        <Link to="/admin/reglages" className="text-bleu-nuit underline underline-offset-2">
          Réglages
        </Link>
      </p>
    </div>
  );
}

/** One « clé : valeur » row of the panel (maquette `.kv`). */
function KeyValue({
  label,
  sub,
  control,
}: {
  label: string;
  sub: string;
  control: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-gray-100 border-t py-2 text-sm first:border-t-0">
      <span className="min-w-0">
        {label}
        <span className="block text-gray-600 text-xs">{sub}</span>
      </span>
      {control}
    </div>
  );
}

/**
 * One of the two repli tiles (maquette `.tile`).
 *
 * The `role="radio"` is on the HEADER of the tile and the extra control sits
 * beside it, never inside: a `<button>` may not contain a `<select>` or an
 * `<input>`, and a browser that flattens the nesting would leave the field
 * unclickable. The tile's border still moves as a whole, which is what the
 * artboard draws.
 */
function Tile({
  testId,
  title,
  sub,
  checked,
  disabled,
  onPick,
  children,
}: {
  testId: string;
  title: string;
  sub: string;
  checked: boolean;
  disabled: boolean;
  onPick: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className={`rounded-md border-2 p-3 ${checked ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-300 bg-white'}`}
    >
      <button
        type="button"
        // A `<button>` with `role="radio"` and not an `<input type="radio">`:
        // the tile carries a text field and a select beside its title, and a
        // `<label>` wrapping a real radio around those would forward a click on
        // the field to the radio. `aria-checked` says the same thing.
        // biome-ignore lint/a11y/useSemanticElements: see above
        role="radio"
        data-testid={testId}
        aria-checked={checked}
        disabled={disabled}
        onClick={onPick}
        className="flex w-full cursor-pointer items-start gap-2.5 text-left disabled:cursor-not-allowed"
      >
        <span
          aria-hidden="true"
          className={`mt-0.5 grid size-4 shrink-0 place-items-center rounded-circle border-2 bg-white ${checked ? 'border-bleu-nuit' : 'border-gray-400'}`}
        >
          {checked ? <span className="size-2 rounded-circle bg-bleu-nuit" /> : null}
        </span>
        <span className="min-w-0">
          <span className={`block font-semibold text-sm ${checked ? 'text-bleu-nuit' : ''}`}>
            {title}
          </span>
          <span className="block text-gray-600 text-xs">{sub}</span>
        </span>
      </button>
      {children === null ? null : <div className="mt-2 pl-6.5">{children}</div>}
    </div>
  );
}
